/**
 * ConfirmDeleteModal: renders a modal to prompt the user for confirmation to delete.
 * Similar to <ConfirmModal>, but adheres to delete modal styling.
 * props:
 *            isOpen (bool): whether the modal is open
 *         headerText (str): text to be rendered in the header of the modal
 *   confirmationText (str): text to be rendered inside the modal
 *      onCancel (function): callback function when the modal is canceled/closed
 *     onConfirm (function): callback function when the modal is confirmed
 *           locale (str): locale for translations (default: 'en')
 */

import React, { useEffect } from 'react';
import I18n from 'i18n-js';
import Modal from './Modal';
import styles from './Modal.module.scss';
import Spinner from './Spinner';
import { Footer } from './index';

type ConfirmDeleteModalProps = {
  confirmButtonText?: string;
  confirmationText?: string;
  headerText: string;
  isLoading: boolean;
  isOpen: boolean;
  locale?: Lowercase<Locale>;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmDeleteModal = ({
  confirmationText = '',
  confirmButtonText,
  headerText,
  isLoading,
  isOpen,
  locale = 'en',
  onCancel,
  onConfirm,
}: ConfirmDeleteModalProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isLoading && isOpen) {
        event.preventDefault();
        onConfirm();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isLoading, onConfirm]);

  const footer = () => (
    <Footer
      primaryButtonCallback={onConfirm}
      primaryButtonClassName="btn btn--red"
      primaryButtonDisabled={isLoading}
      primaryButtonSubmit={false}
      primaryButtonText={confirmButtonText || `${I18n.t('yes_delete', { locale })}`}
      secondaryButtonCallback={onCancel}
      secondaryButtonText={I18n.t('cancel', { locale })}
      wrapperClassName="tw-pr-4 tw-pb-4"
    />
  );

  return (
    <Modal
      className={styles.confirm}
      closeModal={onCancel}
      footer={footer()}
      headerClass={styles.deleteHeader}
      headerText={headerText}
      isOpen={isOpen}
    >
      {isLoading ? <Spinner isLoading={isLoading} /> : confirmationText}
    </Modal>
  );
};

export default ConfirmDeleteModal;
